/**
 * What went wrong changing an artifact's visibility, said to a PERSON.
 *
 * Owner, 2026-09-24, with a screenshot of the dial on prod: 「的提示訊息感覺不對…不應該
 * 是說 no space left on device」. The sheet had printed
 *
 *     That did not take: ENOSPC: no space left on device, write
 *
 * which is a Node `fs` errno with its syscall still attached, handed to a person
 * unedited. He read it as a placeholder for the wrong feature; it was not — the
 * publishing machine really had run out of room and really could not write its
 * artifact store. BOTH halves of that are worth keeping in mind here: the message was
 * a genuine failure AND it was unreadable, and fixing the second must not hide the
 * first.
 *
 * WHY THE VISIBILITY WRITE IS A DISK WRITE AT ALL: an artifact's visibility lives in a
 * file on the machine that publishes it (the edge's artifact store), not in the cloud.
 * So every setting on this dial is someone's filesystem, and every way a filesystem can
 * refuse is a way this control can fail. That is not a leak to paper over — it is the
 * architecture — which is why these sentences name THE MACHINE rather than "the server"
 * or "we".
 *
 * EVERYTHING ELSE KEEPS THE UNDERLYING WORDS, the rule explainPushError.ts already
 * follows: a refusal we have no better sentence for is more useful verbatim than
 * flattened into "something went wrong", because the raw words are what makes the next
 * report diagnosable. Only add a case here when the plain sentence is genuinely more
 * actionable than the code.
 */

/** Machine name interpolated when we have one, so the sentence says WHICH machine. */
export function explainShareError(e: unknown, machine?: string | null): string {
  const msg = String(e instanceof Error ? e.message : e)
  const where = machine ? `${machine} ` : 'That machine '

  /*
   * Matched on the CODE with a boundary, not on the prose. Node writes
   * `ENOSPC: no space left on device, write` today, but the prose after the colon is
   * libuv's and has changed between versions; the code before it has not. A bare
   * /ENOSPC/ would also fire on a path that merely contained the letters, which is why
   * it is anchored to a non-word boundary.
   */
  if (/\bENOSPC\b/.test(msg)) {
    return `${where}has run out of disk space, so it could not save the change. Free some space on it and try again.`
  }
  if (/\bEDQUOT\b/.test(msg)) {
    return `${where}has hit its storage quota, so it could not save the change.`
  }
  if (/\bEROFS\b/.test(msg)) {
    return `${where}cannot write to its own disk right now (the filesystem is read-only).`
  }
  if (/\bEACCES\b|\bEPERM\b/.test(msg)) {
    return `${where}refused to write the change — the agent running there does not have permission to its own artifact store.`
  }
  if (/\bEMFILE\b|\bENFILE\b/.test(msg)) {
    return `${where}has too many files open to save the change. It usually clears on its own; try again in a moment.`
  }
  return msg
}
